// `sign` — the person signs the document a print step FILED (`document` = that
// step's id; the filing is in `vars.<as>`). Signing goes through the
// repository's OWN signing endpoints and the shared SignaturePanel: the same
// re-authentication, meaning, order, role and executed copy as signing on the
// document's page. There is no wizard-side signing path — the repository's
// `view` + `sign` rights decide, never the wizard's grant.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { SignaturePanel } from '../../dms/SignaturePanel'
import type { DmsSignatureStatus } from '../../dms/contracts'
import { stepById, text } from '../model'
import type { WizardFiled } from '../contract'
import { BAD, WARN, banner, muted } from '../styles'
import type { StepProps } from './common'

/** Everything this person can sign here is signed (or nothing is left). */
function mineDone(s: DmsSignatureStatus): boolean {
  if (s.complete) return true
  const open = s.slots.filter(x => x.status !== 'signed' && x.status !== 'skipped')
  const knows = open.some(x => x.can_sign !== undefined)
  return knows ? !open.some(x => x.can_sign) && s.signed > 0 : false
}

export default function SignStep({ ctl }: StepProps) {
  const { step, scope, frame, model, host, ui, setUi } = ctl
  const source = useMemo(() => (model && step?.document ? stepById(model, step.document) : null),
    [model, step?.document])
  const repository = source?.file?.repository || ''
  const filed = (frame?.vars?.[source?.file?.as || 'filed'] || null) as WizardFiled | null
  const documentId = filed?.document_id || ''
  const client = useMemo(() => (repository && host.dmsClient ? host.dmsClient(repository) : null),
    [host, repository])
  const [status, setStatus] = useState<DmsSignatureStatus | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!client?.signatureStatus || !documentId) return
    setError('')
    try {
      let s = await client.signatureStatus(documentId)
      // The campaign is opened here when the document type collects signatures
      // and none is running yet — the doctype's slots, profile and order.
      if (s.enabled && !s.request_id && client.openSignatureRequest) {
        await client.openSignatureRequest(documentId)
        s = await client.signatureStatus(documentId)
      }
      setStatus(s)
      if (mineDone(s) && !ui.signed) setUi({ signed: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [client, documentId, ui.signed, setUi])

  useEffect(() => { void load() }, [load])

  if (!step || !scope) return null
  const say = (tone: typeof BAD, msg: string) => (
    <div style={banner(tone)}><AlertTriangle size={16} /> {msg}</div>
  )
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {step.help && <div style={muted}>{text(step.help, scope)}</div>}
      {!documentId ? say(WARN, 'File the report first — there is nothing to sign yet.')
        : !client || !client.sign || !client.signatureStatus
          ? say(WARN, 'Signing documents is not available here.')
          : error ? say(BAD, error)
            : status && !status.enabled
              ? say(WARN, `The document type in ${repository} does not collect signatures — `
                + 'give it a signature profile in the repository designer.')
              : status && (
                <SignaturePanel status={status} client={client} documentId={documentId}
                  canSign onChanged={() => void load()} />
              )}
      {ui.signed && status?.complete && <div style={muted}>Fully signed.</div>}
    </div>
  )
}
